import React from 'react'
import { FaArrowUp, FaArrowDown, FaMinus } from 'react-icons/fa'
import { SERIES, STATUS, CHROME } from '../lib/viz'
import { signedPercent } from '../lib/format'

/**
 * A headline number, its change, and an optional sparkline.
 *
 * This is the right form for a single current value — a one-bar bar chart would
 * spend a whole panel to encode one number as a length the reader then has to
 * read off an axis anyway.
 *
 * The delta arrow is the load-bearing accessibility detail: up/down is carried
 * by an icon and a signed number, never by green-vs-red alone. Roughly 8% of men
 * cannot reliably separate those two hues, and this is a menswear shop.
 *
 * `goodDirection` exists because "up" is not always good. Rising revenue is;
 * rising out-of-stock lines is not. Hard-coding green-for-up would mislead on
 * exactly the tiles that matter most.
 */

const Sparkline = ({ values, color = SERIES }) => {
  const points = values ?? []
  if (points.length < 2) return null

  const width = 96
  const height = 28
  const max = Math.max(...points)
  const min = Math.min(...points)
  const span = max - min || 1

  const path = points
    .map((value, i) => {
      const x = (i / (points.length - 1)) * width
      const y = height - ((value - min) / span) * (height - 4) - 2
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" className="shrink-0">
      <path d={path} fill="none" stroke={color} strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

const StatTile = ({
  label,
  value,
  change = null,
  changeLabel = 'vs previous period',
  sparkline = null,
  icon: Icon = null,
  goodDirection = 'up',
  hint = null,
  tone = 'neutral', // 'neutral' | 'warning' | 'critical' — for attention tiles
}) => {
  const hasChange = change !== null && change !== undefined && !Number.isNaN(Number(change))
  const rising = hasChange && Number(change) > 0
  const flat = hasChange && Number(change) === 0

  // Does this movement read as good, bad, or neither?
  const isGood = hasChange && !flat && (goodDirection === 'up' ? rising : !rising)
  const deltaColor = flat ? CHROME.inkMuted : isGood ? STATUS.goodText : STATUS.critical
  const DeltaIcon = flat ? FaMinus : rising ? FaArrowUp : FaArrowDown

  const accent =
    tone === 'critical' ? STATUS.critical : tone === 'warning' ? STATUS.warning : SERIES

  return (
    <div className="group relative overflow-hidden rounded-xl border border-hairline bg-surface p-4 shadow-card transition-shadow duration-300 hover:shadow-raised">
      {/* A 2px accent edge, not a coloured card. A whole tinted panel would make
          every tile shout and leave nothing to draw the eye on the one that
          actually needs attention. */}
      <span className="absolute inset-y-0 left-0 w-[2px]" style={{ background: accent }} aria-hidden="true" />

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            {Icon && <Icon className="shrink-0 text-[11px] text-ink-muted" aria-hidden="true" />}
            <span className="truncate text-2xs font-medium uppercase tracking-[0.08em] text-ink-muted">
              {label}
            </span>
          </div>

          <div className="mt-1.5 text-2xl font-semibold leading-none text-ink">{value}</div>

          {hasChange && (
            <div className="mt-2 flex items-center gap-1.5 text-2xs">
              <DeltaIcon className="text-[9px]" style={{ color: deltaColor }} aria-hidden="true" />
              <span className="font-semibold tabular-nums" style={{ color: deltaColor }}>
                {signedPercent(change)}
              </span>
              <span className="truncate text-ink-muted">{changeLabel}</span>
            </div>
          )}

          {!hasChange && hint && <div className="mt-2 text-2xs text-ink-muted">{hint}</div>}
        </div>

        {sparkline && <Sparkline values={sparkline} color={accent} />}
      </div>
    </div>
  )
}

export default StatTile
export { Sparkline }
